/**
 * Onboarding data slice (P1 modules/onboarding/routes.js, exact):
 *   GET  /v1/app/onboarding        → { tenant, progress:{done,total}, steps:[{key, order, title, status, completed_at, updated_at}] }
 *   POST /v1/app/onboarding/:key   { status: 'done'|'skipped'|'todo' } → { changed, live, tenant, progress, steps }
 *        (owner or settings.manage; go_live accepts 'done' only, 409 onboarding_incomplete {pending} until the rest is done)
 * Steps are keyed by shared/onboarding.mjs ONBOARDING_STEPS and finish themselves from events; `onboarding.step_changed`
 * arrives over the stream.
 * Stripe Connect (P6 pay/routes/app.js): POST /v1/app/pay/connect/link → {url} (a fresh Stripe account link; its
 * refresh/return URLs come back to /onboarding?stripe=refresh|return), GET /v1/app/pay/connect/status?refresh=1 → {account}.
 */
import { APP, type Http } from '@app/core/http';
import type { ISO, UUID } from '@app/core/types';

export type StepStatus = 'todo' | 'in_progress' | 'done' | 'skipped';
export interface Step { key: string; order: number; title: string; status: StepStatus; completed_at: ISO | null; updated_at?: ISO | null }
export interface Checklist {
  tenant: { id: UUID; name: string; slug: string; status: string; segment: string | null; live_at: ISO | null };
  progress: { done: number; total: number };
  steps: Step[];
}
export interface SetStepResult extends Checklist { changed: boolean; live: boolean }
/** P6 `publicAccount()`. */
export interface StripeAccount {
  status: 'not_connected' | 'onboarding' | 'active' | 'restricted' | 'disconnected' | string;
  charges_enabled: boolean; payouts_enabled: boolean; details_submitted?: boolean; currently_due?: string[]; disabled_reason?: string | null;
}

export interface Api {
  checklist(): Promise<Checklist>;
  setStep(key: string, status: 'done' | 'skipped' | 'todo', idemKey: string): Promise<SetStepResult>;
  stripeLink(idemKey: string): Promise<{ url: string }>;
  stripeStatus(): Promise<StripeAccount>;
}

export const live = (http: Http): Api => ({
  checklist: () => http.get<Checklist>(`${APP}/onboarding`),
  setStep: (key, status, idemKey) => http.post<SetStepResult>(`${APP}/onboarding/${encodeURIComponent(key)}`, { status }, { idempotencyKey: idemKey }),
  stripeLink: (idemKey) => http.post<{ url: string }>(`${APP}/pay/connect/link`, {}, { idempotencyKey: idemKey }),
  stripeStatus: () => http.get<{ account: StripeAccount }>(`${APP}/pay/connect/status`, { query: { refresh: 1 } }).then((r) => r.account),
});
